/**
 * Player Service - Unified player data access
 * Queries DB first, falls back to FantasyData API if needed
 */

import graphQLApiService from './apiGraphQL';
import fantasyDataService, { formatPlayerForApp, mapStatsFromAPI } from './fantasyDataService';
import { augmentPlayersWithHeadshots } from './headshotsService';

const normalizeProspectRankings = (rankings) => {
  if (!rankings) return null;
  if (typeof rankings === 'string') {
    try {
      return normalizeProspectRankings(JSON.parse(rankings));
    } catch {
      return null;
    }
  }
  if (Array.isArray(rankings)) {
    return rankings.reduce((result, item) => {
      const source = item.source || item.name || item.provider || item.key;
      const rank = item.rank ?? item.position ?? item.value;
      if (source && rank != null && !Number.isNaN(Number(rank))) {
        result[source] = Number(rank);
      }
      return result;
    }, {});
  }
  if (typeof rankings === 'object') {
    return Object.entries(rankings).reduce((result, [source, value]) => {
      const rank = value?.rank ?? value?.position ?? value?.value ?? value;
      if (rank != null && !Number.isNaN(Number(rank))) {
        result[source] = Number(rank);
      }
      return result;
    }, {});
  }
  return null;
};

const getProspectEligibility = (player) => {
  if (!player) return false;
  const status = (player.status || player.stats?.status || '').toString().toLowerCase();
  const isExplicitEligible = player.prospectEligible || player.stats?.prospectEligible;
  if (isExplicitEligible) return true;
  if (status.includes('minor') || status.includes('rookie') || status.includes('prospect')) {
    return true;
  }
  const birthDate = player.birthDate || player.stats?.birthDate;
  if (birthDate) {
    const dob = new Date(birthDate);
    if (!Number.isNaN(dob.getTime())) {
      const age = Math.floor((Date.now() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
      if (age <= 23) return true;
    }
  }
  const yearsPro = player.yearsPro || player.stats?.yearsPro || player.stats?.experience;
  if (yearsPro != null && !Number.isNaN(Number(yearsPro)) && Number(yearsPro) <= 2) {
    return true;
  }
  return false;
};

const buildProspectMetadata = (player) => {
  const rawRankings = player.prospectRankings || player.stats?.prospectRankings || player.stats?.rankings || player.stats?.prospectRanks || player.rankings;
  const prospectRankings = normalizeProspectRankings(rawRankings);
  const prospectSources = prospectRankings
    ? Object.entries(prospectRankings)
      .map(([source, rank]) => ({ source, rank }))
      .sort((a, b) => a.rank - b.rank)
    : [];
  const topProspectRanking = prospectSources[0] || null;
  const prospectEligible = getProspectEligibility(player);

  return {
    prospectRankings,
    prospectSources,
    topProspectRanking,
    prospectEligible,
    prospectSummary: prospectSources.length > 0
      ? prospectSources.map(({ source, rank }) => `${source} #${rank}`).join(' • ')
      : null,
  };
};

/**
 * Format DB player to app format
 * Handles both detailed schema (displayName, firstName, etc.) and simpler schema (name, stats)
 */
const formatDBPlayerForApp = (dbPlayer) => {
  const stats = dbPlayer.stats || {};
  const nameParts = (dbPlayer.name || dbPlayer.displayName || `${dbPlayer.firstName || ''} ${dbPlayer.lastName || ''}`.trim()).split(' ');
  const firstName = dbPlayer.firstName || nameParts[0] || '';
  const lastName = dbPlayer.lastName || nameParts.slice(1).join(' ') || '';
  const displayName = dbPlayer.displayName || dbPlayer.name || `${firstName} ${lastName}`.trim();

  const player = {
    id: dbPlayer.playerId || dbPlayer.fantasyDataPlayerId?.toString(),
    playerId: dbPlayer.playerId,
    fantasyDataPlayerId: dbPlayer.fantasyDataPlayerId ?? stats.fantasyNerdsId,
    name: displayName,
    firstName,
    lastName,
    position: dbPlayer.position || 'NA',
    positions: dbPlayer.positions || stats.positions || [dbPlayer.position].filter(Boolean),
    team: dbPlayer.team || 'FA',
    teamId: dbPlayer.teamId,
    number: dbPlayer.jersey,
    status: dbPlayer.status || stats.status || 'Active',
    isPitcher: ['P', 'SP', 'RP'].includes(dbPlayer.position || ''),
    height: dbPlayer.height ?? stats.height,
    weight: dbPlayer.weight ?? stats.weight,
    birthDate: dbPlayer.birthDate ?? stats.birthDate,
    age: dbPlayer.age ?? stats.age,
    photoUrl: dbPlayer.photoUrl || dbPlayer.headshotUrl,
    injuryStatus: dbPlayer.injuryStatus,
    injuryDetails: dbPlayer.injuryDetails,
    seasonStats: dbPlayer.seasonStats,
    lastUpdatedStats: dbPlayer.lastUpdatedStats,
    image: dbPlayer.photoUrl || dbPlayer.headshotUrl,
    stats,
    prospectRankings: dbPlayer.prospectRankings,
    rankings: dbPlayer.rankings,
    prospectEligible: dbPlayer.prospectEligible,
  };

  return {
    ...player,
    ...buildProspectMetadata(player),
  };
};

/**
 * Format API player to app format (with fallback for missing fields)
 */
const formatAPIPlayerForApp = (apiPlayer) => {
  const formatted = formatPlayerForApp(apiPlayer);
  
  const player = {
    ...formatted,
    playerId: formatted.id?.toString(),
    fantasyDataPlayerId: formatted.id,
    displayName: formatted.name,
    firstName: formatted.name?.split(' ')[0] || '',
    lastName: formatted.name?.split(' ').slice(1).join(' ') || '',
    positions: formatted.position ? [formatted.position] : [],
    isPitcher: ['P', 'SP', 'RP'].includes(formatted.position),
    stats: formatted.stats,
    prospectRankings: formatted.prospectRankings,
    prospectEligible: formatted.prospectEligible,
  };

  return {
    ...player,
    ...buildProspectMetadata(player),
  };
};

/**
 * Get all players - DB first, API fallback
 */
export const getAllPlayers = async (options = {}) => {
  const { 
    useCache = true,
    forceRefresh = false,
    filters = {},
  } = options;

  try {
    // Try DB first (unless force refresh)
    if (!forceRefresh) {
      console.log('📦 Fetching players from database...');
      
      try {
        const result = await graphQLApiService.getPlayers(filters);
        
        if (result.success && result.players && result.players.length > 0) {
          console.log(`✅ Found ${result.players.length} players in database`);
          
          // Format DB players for app
          const formattedPlayers = result.players
            .map(formatDBPlayerForApp)
            .filter(p => {
              // Apply filters
              if (filters.position && p.position !== filters.position) return false;
              if (filters.team && p.team !== filters.team) return false;
              if (filters.status && p.status !== filters.status) return false;
              if (filters.activeOnly && p.status !== 'Active') return false;
              return true;
            });
          
          await augmentPlayersWithHeadshots(formattedPlayers);
          return {
            success: true,
            players: formattedPlayers,
            source: 'database',
            count: formattedPlayers.length,
          };
        }
      } catch (dbError) {
        console.warn('⚠️ Database query failed, falling back to API:', dbError.message);
      }
    }

    // Fallback to API
    console.log('📡 Fetching players from FantasyData API...');
    
    const apiPlayers = await fantasyDataService.getActivePlayers();
    
    if (!apiPlayers || !Array.isArray(apiPlayers)) {
      throw new Error('Invalid response from FantasyData API');
    }
    
    console.log(`✅ Loaded ${apiPlayers.length} players from API`);
    
    // Format API players
    const formattedPlayers = apiPlayers
      .filter(p => {
        // Apply filters
        if (filters.activeOnly && p.Status !== 'Active') return false;
        if (filters.position && p.Position !== filters.position) return false;
        if (filters.team && p.Team !== filters.team) return false;
        return true;
      })
      .map(formatAPIPlayerForApp);

    await augmentPlayersWithHeadshots(formattedPlayers);
    return {
      success: true,
      players: formattedPlayers,
      source: 'api',
      count: formattedPlayers.length,
    };
    
  } catch (error) {
    console.error('❌ Error fetching players:', error);
    
    // Provide more helpful error messages
    let errorMessage = error.message || 'Failed to fetch players';
    if (errorMessage.includes('403') || errorMessage.includes('Fantasy Data API Error: 403')) {
      // Check if it's a quota issue or API key issue
      if (errorMessage.includes('quota') || errorMessage.includes('rate limit') || errorMessage.includes('429')) {
        errorMessage = 'FantasyData API quota has been exceeded. Please try again later or upgrade your API plan.';
      } else {
        errorMessage = 'FantasyData API key not configured. Please set up the API key secret in AWS Secrets Manager. See documentation/FANTASY_DATA_API_KEY_SETUP.md for instructions.';
      }
    } else if (errorMessage.includes('429') || errorMessage.includes('quota') || errorMessage.includes('rate limit')) {
      errorMessage = 'FantasyData API quota has been exceeded. Please try again later or upgrade your API plan.';
    }
    
    return {
      success: false,
      error: errorMessage,
      players: [],
      source: null,
      count: 0,
    };
  }
};

/**
 * Fetch all players from the player table only (no Fantasy Data API fallback).
 * Pages through getPlayersPage to load full list.
 */
export const getAllPlayersFromDB = async (options = {}) => {
  const { filters = {} } = options;
  const allPlayers = [];
  let nextToken = null;

  try {
    do {
      const result = await graphQLApiService.getPlayersPage({
        limit: 100,
        nextToken: nextToken || undefined,
        filters,
      });
      if (!result.success || !result.players) {
        return {
          success: false,
          error: result.error || 'Failed to fetch players from database',
          players: [],
          source: 'database',
        };
      }
      const formatted = (result.players || []).map(formatDBPlayerForApp);
      const toAdd =
        filters.status && filters.status !== 'all'
          ? formatted.filter((p) => (p.status || 'Active') === filters.status)
          : formatted;
      toAdd.forEach((p) => allPlayers.push(p));
      nextToken = result.nextToken ?? null;
    } while (nextToken);

    await augmentPlayersWithHeadshots(allPlayers);
    return {
      success: true,
      players: allPlayers,
      count: allPlayers.length,
      source: 'database',
    };
  } catch (error) {
    console.error('getAllPlayersFromDB error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch players from database',
      players: [],
      source: 'database',
    };
  }
};

/**
 * Get players for draft - from player table only (no Fantasy Data API).
 */
export const getDraftPlayers = async (options = {}) => {
  const { excludeRostered = [], filters = {} } = options;

  const result = await getAllPlayersFromDB({
    filters: { ...filters },
  });

  if (!result.success || !result.players) {
    return result;
  }

  // Filter out already rostered / drafted players
  const availablePlayers = result.players.filter((p) => {
    if (!excludeRostered || excludeRostered.length === 0) return true;
    return (
      !excludeRostered.includes(p.id?.toString()) &&
      !excludeRostered.includes(p.playerId) &&
      !excludeRostered.includes(p.fantasyDataPlayerId?.toString())
    );
  });

  return {
    ...result,
    players: availablePlayers,
    count: availablePlayers.length,
  };
};

/**
 * Get player by ID - DB first, API fallback
 */
export const getPlayerById = async (playerId) => {
  try {
    // Try DB first
    try {
      const result = await graphQLApiService.getPlayer(playerId);
      
      if (result.success && result.player) {
        const player = formatDBPlayerForApp(result.player);
        await augmentPlayersWithHeadshots([player]);
        return {
          success: true,
          player,
          source: 'database',
        };
      }
    } catch (dbError) {
      console.warn('⚠️ Player not found in database, trying API:', dbError.message);
    }
    
    // Fallback to API - would need to fetch all players and find the one
    // For now, return error - player should be in DB
    return {
      success: false,
      error: 'Player not found',
      source: null,
    };
    
  } catch (error) {
    console.error('❌ Error fetching player:', error);
    return {
      success: false,
      error: error.message,
      source: null,
    };
  }
};

/**
 * Search players by name
 */
export const searchPlayers = async (searchTerm, options = {}) => {
  const { limit = 50 } = options;
  
  if (!searchTerm || searchTerm.trim().length === 0) {
    return {
      success: true,
      players: [],
      count: 0,
    };
  }
  
  const result = await getAllPlayers({
    filters: options.filters || {},
  });
  
  if (!result.success || !result.players) {
    return result;
  }
  
  const searchLower = searchTerm.toLowerCase().trim();
  const matchedPlayers = result.players
    .filter(p => {
      const name = (p.name || `${p.firstName} ${p.lastName}`).toLowerCase();
      const firstName = (p.firstName || '').toLowerCase();
      const lastName = (p.lastName || '').toLowerCase();
      
      return name.includes(searchLower) ||
             firstName.includes(searchLower) ||
             lastName.includes(searchLower);
    })
    .slice(0, limit);
  
  return {
    ...result,
    players: matchedPlayers,
    count: matchedPlayers.length,
  };
};

/**
 * Get players by position
 */
export const getPlayersByPosition = async (position, options = {}) => {
  return getAllPlayers({
    ...options,
    filters: {
      ...options.filters,
      position,
    },
  });
};

/**
 * Get players by team
 */
export const getPlayersByTeam = async (team, options = {}) => {
  return getAllPlayers({
    ...options,
    filters: {
      ...options.filters,
      team,
    },
  });
};

/**
 * Get a page of players from the DB only (server-side limit, no API fallback).
 * Use for Players tab pagination. Returns { success, players, nextToken, count }.
 */
export const getPlayersPage = async (options = {}) => {
  const { limit = 50, nextToken, filters = {} } = options;

  try {
    const result = await graphQLApiService.getPlayersPage({
      limit,
      nextToken: nextToken || undefined,
      filters,
    });

    if (!result.success || !result.players) {
      return {
        success: false,
        error: result.error || 'Failed to fetch players page',
        players: [],
        nextToken: null,
        count: 0,
      };
    }

    let formattedPlayers = (result.players || []).map(formatDBPlayerForApp);

    // Client-side status filter (deployed schema has no top-level status)
    if (filters.status && filters.status !== 'all') {
      formattedPlayers = formattedPlayers.filter((p) => (p.status || 'Active') === filters.status);
    }

    await augmentPlayersWithHeadshots(formattedPlayers);

    return {
      success: true,
      players: formattedPlayers,
      nextToken: result.nextToken ?? null,
      count: formattedPlayers.length,
    };
  } catch (error) {
    console.error('Error fetching players page:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch players page',
      players: [],
      nextToken: null,
      count: 0,
    };
  }
};

// Export default service object
export default {
  getAllPlayers,
  getAllPlayersFromDB,
  getDraftPlayers,
  getPlayerById,
  searchPlayers,
  getPlayersByPosition,
  getPlayersByTeam,
  getPlayersPage,
};

